import { useState, useEffect } from "react";
import {
  listTenants,
  listKbSuggestions,
  approveKbSuggestion,
  rejectKbSuggestion,
} from "../lib/api";
import type { Tenant, KbSuggestion, KbChangelogStatus } from "../types";

const STATUS_TABS: { value: KbChangelogStatus | "all"; label: string }[] = [
  { value: "pending", label: "待審批" },
  { value: "saved_for_pro", label: "已保存（Lite）" },
  { value: "approved", label: "已採納" },
  { value: "rejected", label: "已忽略" },
  { value: "all", label: "全部" },
];

function fmtTime(ts: number) {
  if (!ts) return "—";
  const n = ts < 1e12 ? ts * 1000 : ts;
  return new Date(n).toLocaleString("zh-HK");
}

export default function KbLearningPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [suggestions, setSuggestions] = useState<KbSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tenantFilter, setTenantFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("pending");

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editContent, setEditContent] = useState("");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [actionMessage, setActionMessage] = useState("");

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    loadSuggestions();
  }, [tenantFilter, statusFilter]);

  async function loadTenants() {
    try {
      const data = await listTenants();
      if (data.success) setTenants(data.tenants || []);
    } catch {
      // 靜默失敗，租戶篩選器只作輔助
    }
  }

  async function loadSuggestions() {
    setLoading(true);
    setError("");
    try {
      const data = await listKbSuggestions(
        tenantFilter || undefined,
        statusFilter === "all" ? undefined : statusFilter,
        100
      );
      if (data.success) {
        setSuggestions(data.suggestions || []);
      } else {
        setError(data.error || "載入失敗");
      }
    } catch {
      setError("網路錯誤");
    } finally {
      setLoading(false);
    }
  }

  function startEdit(s: KbSuggestion) {
    setEditingId(s.id);
    setEditQuestion(s.suggested_question);
    setEditContent(s.suggested_content);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditQuestion("");
    setEditContent("");
  }

  async function handleApprove(s: KbSuggestion, useEdits: boolean) {
    setActionMessage("");
    try {
      const updates =
        useEdits && editingId === s.id
          ? { suggested_question: editQuestion, suggested_content: editContent }
          : undefined;
      const data = await approveKbSuggestion(s.id, updates);
      if (data.success) {
        if (data.saved) {
          setActionMessage(`💾 建議 #${s.id} 已保存（Lite 租戶暫不入庫），升 Pro 後會一次過應用`);
        } else {
          setActionMessage(`✅ 建議 #${s.id} 已採納並寫入知識庫`);
        }
        setEditingId(null);
        await loadSuggestions();
      } else {
        setActionMessage(`❌ 採納失敗：${data.error || "未知錯誤"}`);
      }
    } catch {
      setActionMessage("❌ 採納失敗：網路錯誤");
    }
  }

  async function handleReject(s: KbSuggestion) {
    setActionMessage("");
    try {
      const data = await rejectKbSuggestion(s.id);
      if (data.success) {
        setActionMessage(`🗑️ 建議 #${s.id} 已忽略`);
        await loadSuggestions();
      } else {
        setActionMessage(`❌ 操作失敗：${data.error || "未知錯誤"}`);
      }
    } catch {
      setActionMessage("❌ 操作失敗：網路錯誤");
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-100">🧠 AI 學習審核</h1>
        <button
          onClick={loadSuggestions}
          className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
        >
          🔄 重新載入
        </button>
      </div>

      <p className="text-sm text-slate-400 mb-4">
        人工接管結束後，AI 會分析對話並產生知識庫更新建議。採納後才會寫入知識庫（Vectorize），不會自動覆蓋現有內容。
      </p>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-emerald-500"
        >
          <option value="">全部租戶</option>
          {tenants.map((t) => (
            <option key={t.tenant_id} value={t.tenant_id}>
              {t.tenant_id}
            </option>
          ))}
        </select>

        <div className="flex rounded-lg overflow-hidden border border-slate-700">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-4 py-2 text-sm transition-colors ${
                statusFilter === tab.value
                  ? "bg-emerald-600/30 text-emerald-300"
                  : "bg-slate-800 text-slate-400 hover:bg-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {actionMessage && (
        <div className="mb-4 p-3 rounded-lg bg-slate-800/60 border border-slate-700 text-sm text-slate-200">
          {actionMessage}
        </div>
      )}

      {loading ? (
        <div className="text-slate-400">載入中...</div>
      ) : error ? (
        <div className="text-rose-400">❌ {error}</div>
      ) : suggestions.length === 0 ? (
        <div className="text-slate-500">目前沒有符合條件的建議。</div>
      ) : (
        <div className="space-y-4">
          {suggestions.map((s) => {
            const isEditing = editingId === s.id;
            const isExpanded = expandedId === s.id;
            /* W-13：undo 撤銷（status=rejected＋reviewed_by=undo）與普通忽略分流——
               自動入庫 undo 出現喺「已撤銷」而唔係「已忽略」 */
            const isUndone = s.status === "rejected" && s.reviewed_by === "undo";
            const statusBadge =
              s.status === "pending"
                ? "bg-amber-500/20 text-amber-400"
                : s.status === "approved"
                ? "bg-emerald-500/20 text-emerald-400"
                : s.status === "saved_for_pro"
                ? "bg-lime-500/20 text-lime-400"
                : isUndone
                ? "bg-sky-500/20 text-sky-400"
                : "bg-slate-600/40 text-slate-400";
            const statusLabel =
              s.status === "pending"
                ? "待審批"
                : s.status === "approved"
                ? "已採納"
                : s.status === "saved_for_pro"
                ? "已保存（Lite）"
                : isUndone
                ? "已撤銷（auto-apply undo）"
                : "已忽略";

            return (
              <div
                key={s.id}
                className="bg-slate-900 rounded-xl border border-slate-800 p-5"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-emerald-400 text-xs">#{s.id}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge}`}>
                        {statusLabel}
                      </span>
                      <span className="text-xs text-slate-500">
                        {s.tenant_id} · {s.session_id}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      建立：{fmtTime(s.created_at)}
                      {s.reviewed_at ? ` · 審批：${fmtTime(s.reviewed_at)}` : ""}
                    </div>
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-3 mb-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">問題</label>
                      <input
                        value={editQuestion}
                        onChange={(e) => setEditQuestion(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">回答</label>
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        rows={5}
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <div className="text-sm font-medium text-slate-200 mb-1">
                      Q：{s.suggested_question}
                    </div>
                    <div className="text-sm text-slate-300 whitespace-pre-wrap">
                      A：{s.suggested_content}
                    </div>
                  </div>
                )}

                {s.ai_reason && (
                  <div className="text-xs text-slate-500 mb-2">AI 分析原因：{s.ai_reason}</div>
                )}

                {s.source_transcript && (
                  <div className="mb-3">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : s.id)}
                      className="text-xs text-sky-400 hover:text-sky-300"
                    >
                      {isExpanded ? "▲ 收起来源對話" : "▼ 查看來源對話"}
                    </button>
                    {isExpanded && (
                      <pre className="mt-2 p-3 bg-slate-950 rounded-lg text-xs text-slate-400 whitespace-pre-wrap max-h-64 overflow-auto">
                        {s.source_transcript}
                      </pre>
                    )}
                  </div>
                )}

                {s.status === "saved_for_pro" && (
                  <div className="mb-3 p-3 rounded-lg bg-lime-500/10 border border-lime-500/20 text-sm text-lime-300">
                    此建議來自 Lite 租戶，已保存但未寫入知識庫。租戶升級 Pro 後可一次過應用。
                  </div>
                )}

                {s.status === "pending" && (
                  <div className="flex flex-wrap gap-2">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => handleApprove(s, true)}
                          className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
                        >
                          ✅ 採納（已修改）
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors"
                        >
                          取消
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleApprove(s, false)}
                          className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
                        >
                          ✅ 採納
                        </button>
                        <button
                          onClick={() => startEdit(s)}
                          className="px-4 py-2 text-sm bg-sky-600/30 hover:bg-sky-600/40 text-sky-300 rounded-lg transition-colors"
                        >
                          ✏️ 修改後採納
                        </button>
                        <button
                          onClick={() => handleReject(s)}
                          className="px-4 py-2 text-sm bg-rose-600/20 hover:bg-rose-600/30 text-rose-400 rounded-lg transition-colors"
                        >
                          🗑️ 忽略
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
